import React, { useState } from 'react';
import { Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import './Contact.css';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    interest: 'consulting'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // MOCK: In real implementation, this will call backend API
    setTimeout(() => {
      toast({
        title: "Message Sent!",
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        interest: 'consulting'
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-hero-section">
        <div className="section-content">
          <h1 className="display-huge">Let's Talk</h1>
          <p className="body-large contact-hero-subtitle">
            Ready to upgrade your business? Book a free strategy call or send us a message.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="contact-main-section">
        <div className="section-content">
          <div className="contact-grid">
            {/* Contact Form */}
            <Card className="contact-form-card">
              <h2 className="heading-1">Send a Message</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Your name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="your@email.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number</label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="+1 307-427-2444"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company" className="form-label">Company Name</label>
                  <Input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Your company"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="interest" className="form-label">I'm Interested In *</label>
                  <select
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    required
                    className="form-select"
                  >
                    <option value="consulting">Consulting Services</option>
                    <option value="community">Community Membership</option>
                    <option value="both">Both Consulting & Community</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="form-textarea"
                    placeholder="Tell us about your business and what you're looking to achieve..."
                    rows={6}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting}
                >
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  <Send size={20} />
                </Button>
              </form>
            </Card>

            {/* Contact Info */}
            <div className="contact-info-section">
              <div className="contact-info-card">
                <h2 className="heading-1">Get In Touch</h2>
                <p className="body-medium contact-info-text">
                  Prefer to reach out directly? Use any of the options below.
                </p>

                <div className="contact-methods">
                  <div className="contact-method">
                    <div className="contact-method-icon">
                      <Mail size={24} />
                    </div>
                    <div className="contact-method-info">
                      <div className="contact-method-label">Email</div>
                      <a href="mailto:hello@discoverbusiness-class.com" className="contact-method-value">
                        hello@discoverbusiness-class.com
                      </a>
                    </div>
                  </div>

                  <div className="contact-method">
                    <div className="contact-method-icon">
                      <Phone size={24} />
                    </div>
                    <div className="contact-method-info">
                      <div className="contact-method-label">Phone</div>
                      <a href="tel:+13074272444" className="contact-method-value">
                        +1 307-427-2444
                      </a>
                    </div>
                  </div>
                  
                  <div className="contact-method">
                    <div className="contact-method-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                    </div>
                    <div className="contact-method-info">
                      <div className="contact-method-label">Address</div>
                      <div className="contact-method-value">
                        30 N Gould Street STE R<br />
                        Sheridan, Wyoming, 82801
                      </div>
                    </div>
                  </div>
                </div>

                <div className="contact-info-cta">
                  <h3 className="heading-2">Book a Strategy Call</h3>
                  <p className="body-small">
                    Schedule a free 30-minute consultation to discuss your business goals.
                  </p>
                  <Button className="btn-secondary w-full mt-4">
                    <span>Schedule Call</span>
                    <CheckCircle size={20} />
                  </Button>
                </div>
              </div>

              <div className="response-time-card">
                <CheckCircle size={32} className="response-icon" />
                <h3 className="heading-2">Quick Response Time</h3>
                <p className="body-medium">
                  We typically respond within 24 hours on business days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="contact-faq-section">
        <div className="section-content">
          <h2 className="display-medium text-center">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3 className="heading-3">Who do you work with?</h3>
              <p className="body-medium">
                We work with ambitious founders and business owners scaling from ₹10L to ₹10Cr+. 
                Our ideal clients are service businesses, education companies, and startups ready 
                to systemize and scale.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="heading-3">How long are engagements?</h3>
              <p className="body-medium">
                Consulting engagements typically range from 3-12 months depending on scope. 
                Community memberships are month-to-month with no long-term commitment required.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="heading-3">What results can I expect?</h3>
              <p className="body-medium">
                Our clients typically see 2-3x revenue growth within 18 months, significant 
                operational efficiency improvements, and clearer strategic direction.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="heading-3">Do you work remotely?</h3>
              <p className="body-medium">
                Yes, we work with clients across India and internationally. Most of our work 
                is done remotely, with in-person workshops available for local clients.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
